import React from "react";
import styles from "./ResumeTemplateClassic.module.css";
import {
  getJobLocationById,
  getWorkExpText,
  toArray,
} from "../../../../helpers/common";
import {
  getEndData,
  getStartData,
} from "../../../Account/Candidate/MyProfile/ExperienceJourney";
import WebsiteLogo from "../../../../helpers/WebsiteLogo";
import { getCertificationValidity } from "..";

/**
 * Helper function to safely extract start and end dates/years from education objects
 */
const formatEducationDate = (fields) => {
  if (!fields) return "";

  // Extract start date / year
  const start =
    fields.start_year ||
    fields.startYear ||
    fields.start_date ||
    fields.fromDate ||
    fields.from_year;

  // Extract end date / passing year
  const end =
    fields.end_year ||
    fields.endYear ||
    fields.end_date ||
    fields.toDate ||
    fields.to_year ||
    fields.passout_year ||
    fields.passing_year ||
    fields.year_of_passing ||
    fields.completion_year;

  // Check pursuing status
  const isPursuing =
    fields.currently_studying ||
    fields.is_current ||
    fields.pursuing ||
    fields.currently_pursuing;

  if (start && end) return `${start} - ${end}`;
  if (start && isPursuing) return `${start} - Present`;
  if (start) return `${start}`;
  if (end) return `${end}`;

  // Helper fallback check
  const startFromHelper = getStartData(fields);
  const endFromHelper = getEndData(fields);

  if (
    startFromHelper &&
    endFromHelper &&
    startFromHelper !== "undefined" &&
    endFromHelper !== "undefined"
  ) {
    return `${startFromHelper} - ${endFromHelper}`;
  }

  return "";
};

const Header = ({ formData, jobLocations, experiences }) => {
  // Extract position fallback from most recent work experience if direct designation is missing
  let jobTitle =
    formData?.designation ||
    formData?.job_title ||
    formData?.title ||
    formData?.current_designation;

  if (!jobTitle && experiences && experiences.length > 0) {
    const latestJob = experiences[0];
    const fields = latestJob?.additional_fields || latestJob;
    jobTitle = fields?.position || fields?.designation || fields?.job_title;
  }

  const locationName = formData?.location
    ? getJobLocationById(formData.location, jobLocations)
    : "";

  return (
    <div className={styles.header}>
      <h1 className={styles.name}>{formData?.name || "Your Name"}</h1>
      <div className={styles.contact}>
        {locationName && <>{locationName}, India | </>}
        {formData?.email && (
          <>
            <a href={`mailto:${formData.email}`}>{formData.email}</a> |{" "}
          </>
        )}
        {formData?.mobile_number && <>{formData.mobile_number}</>}
      </div>
      {jobTitle && <div className={styles.title}>{jobTitle}</div>}
    </div>
  );
};

const Skills = ({ skills }) => {
  const rawList = Array.isArray(skills)
    ? skills
    : skills?.skills || skills?.additional_fields?.skills || skills;

  let parsedArray = [];
  if (typeof rawList === "string") {
    parsedArray = toArray(rawList, ",");
  } else if (Array.isArray(rawList)) {
    parsedArray = rawList;
  } else {
    parsedArray = toArray(rawList);
  }

  const skillsArr = parsedArray
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (typeof item === "object" && item !== null) {
        return (
          item?.skill_name ||
          item?.name ||
          item?.skill ||
          item?.title ||
          ""
        ).trim();
      }
      return "";
    })
    .filter(Boolean)
    .slice(0, 8);

  if (!skillsArr?.length) return null;

  const mid = Math.ceil(skillsArr.length / 2);
  const col1 = skillsArr.slice(0, mid);
  const col2 = skillsArr.slice(mid);

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>KEY SKILLS</div>
        <div className={styles.skillsGrid}>
          <div className={styles.skillColumn}>
            <ul className={styles.bulletList}>
              {col1.map((skill, i) => (
                <li className={styles.skillItem} key={i}>
                  {skill}
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.skillColumn}>
            <ul className={styles.bulletList}>
              {col2.map((skill, i) => (
                <li className={styles.skillItem} key={i}>
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = ({ projects }) => {
  if (!projects?.length) return null;

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>PROJECTS</div>
        {projects.map((project, idx) => {
          const fields = project?.additional_fields || project;
          const title = fields?.title || fields?.project_title || fields?.name;
          const organization =
            fields?.organization || fields?.company || fields?.client;
          const details = fields?.details;
          const date = fields?.date || fields?.duration;

          return (
            <div className={styles.projectItem} key={idx}>
              <div className={styles.projectHeader}>
                <h3 className={styles.projectTitle}>
                  {title}
                  {organization ? ` | ${organization}` : ""}
                </h3>
                {date && <span className={styles.date}>{date}</span>}
              </div>
              {details && (
                <ul className={styles.bulletList}>
                  {toArray(details, "\n").map((item, i) => (
                    <li className={styles.bulletItem} key={i}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Education = ({ educations }) => {
  if (!educations?.length) return null;

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>EDUCATION</div>
        {educations.map((education, idx) => {
          const fields = education?.additional_fields || education;

          const degreeTitle =
            fields?.title ||
            fields?.degree ||
            fields?.qualification ||
            fields?.course;

          const schoolName =
            fields?.university_college ||
            fields?.university ||
            fields?.college ||
            fields?.school ||
            fields?.institution;

          const dateRange = formatEducationDate(fields);
          const certification = fields?.certification;
          const gpa = fields?.gpa;
          const details = fields?.details;

          return (
            <div className={styles.educationItem} key={idx}>
              <div className={styles.educationHeader}>
                <div>
                  {degreeTitle && (
                    <h3 className={styles.degreeTitle}>{degreeTitle}</h3>
                  )}
                  {schoolName && <span>{schoolName}</span>}
                </div>
                {dateRange && <span className={styles.date}>{dateRange}</span>}
              </div>
              <ul className={styles.bulletList}>
                {certification && (
                  <li className={styles.bulletItem}>
                    Certification: {certification}
                  </li>
                )}
                {gpa && <li className={styles.bulletItem}>GPA: {gpa}</li>}
                {details &&
                  toArray(details, "\n").map((item, i) => (
                    <li className={styles.bulletItem} key={i}>
                      {item}
                    </li>
                  ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Experience = ({ experience, resumeBuilder }) => {
  if (!experience?.length) return null;

  const rawExpText = resumeBuilder?.total_work_experience
    ? getWorkExpText(resumeBuilder?.total_work_experience)?.trim()
    : "";

  const totalExperience =
    rawExpText && rawExpText !== "years" && rawExpText !== "year"
      ? `(${rawExpText})`
      : "";

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>
          WORK EXPERIENCE {totalExperience}
        </div>
        {experience.map((job, idx) => {
          const jobData = job?.additional_fields || job;

          const startDate = getStartData(jobData);
          const endDate = getEndData(jobData);

          let dateDisplay = "";
          if (startDate && endDate) {
            dateDisplay =
              endDate === "Present"
                ? `${startDate} - Present`
                : `${startDate} - ${endDate}`;
          } else if (startDate) {
            dateDisplay = startDate;
          } else if (endDate) {
            dateDisplay = endDate;
          }

          const position =
            jobData?.position || jobData?.designation || jobData?.job_title;
          const company =
            jobData?.company_name || jobData?.company || jobData?.organization;
          const details = jobData?.details;

          return (
            <div className={styles.workItem} key={idx}>
              <div className={styles.workHeader}>
                <h3 className={styles.jobTitle}>
                  {position}
                  {company ? ` | ${company}` : ""}
                </h3>
                {dateDisplay && <span className={styles.date}>{dateDisplay}</span>}
              </div>

              {details && (
                <ul className={styles.bulletList}>
                  {toArray(details, "\n").map((detail, i) => (
                    <li className={styles.bulletItem} key={i}>
                      {detail}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Certification = ({ certifications }) => {
  if (!certifications?.length) return null;

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>CERTIFICATIONS</div>
        <ul className={styles.bulletList}>
          {certifications.map((cert, idx) => {
            const fields = cert?.additional_fields || cert;
            const title = fields?.title || fields?.name || fields?.certificate_name;
            const organization = fields?.organization || fields?.issuer;
            const url = fields?.url || fields?.link;

            return (
              <li className={styles.awardItem} key={idx}>
                {title && <strong>{title}</strong>}
                {organization && (
                  <span className="ms-2">
                    <strong>| {organization}</strong>
                  </span>
                )}

                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2"
                  >
                    <i className="uil uil-external-link-alt"></i>
                  </a>
                )}

                <div style={{ fontSize: "0.95em", color: "#666" }}>
                  {getCertificationValidity(cert)}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const Awards = ({ awards }) => {
  if (!awards?.length) return null;

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <div className={styles.sectionHeader}>AWARDS & ACHIEVEMENTS</div>
        <ul className={styles.bulletList}>
          {awards.map((award, idx) => {
            const text =
              typeof award === "string"
                ? award
                : award?.title || award?.name || award?.additional_fields?.title;

            return (
              <li className={styles.awardItem} key={idx}>
                {text}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const ResumeTemplateClassic = ({
  formData,
  jobLocations,
  experiences,
  certifications,
  qualifications,
}) => {
  const projects = formData?.projects || [];
  const awards = formData?.awards || [];

  return (
    <div className={styles.resume}>
      <Header
        formData={formData}
        jobLocations={jobLocations}
        experiences={experiences}
      />

      <div className={styles.container}>
        <Skills skills={formData?.skills} />
        <Projects projects={projects} />
        <Experience experience={experiences} resumeBuilder={formData} />
        <Education educations={qualifications} />
        <Certification certifications={certifications} />
        <Awards awards={awards} />

        {/* Resume Footer */}
        <div className="resume-footer">
          Generated by{" "}
          <a href="https://webvi.in/" style={{ color: "#888" }}>
            <WebsiteLogo />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResumeTemplateClassic;